<template>
  <div class="vb-result">
    <not-found
      v-if="products.length === 0"
      icon="lens-blocked"
      :text="{
        title: '<strong>Oops!</strong><br/>Non abbiamo trovato risultati.',
        message: `Sembra che non ci siano ${is_only_stay_package ? 'hotel' : 'disponibilità'} che corrispondono alla tua ricerca.`,
        suggestion:
          'Prova a modificare la ricerca o esplora nuove destinazioni.',
      }"
    />

    <b-row v-else>
      <b-col
        cols="12"
        xl="3"
        class="vb-result__sidebar"
        :class="{ visible: show_filters }"
      >
        <filters-sidebar
          v-if="!loading"
          :filters="filters"
          :active_filters="active_filters_map"
          :update_key="update_key"
          :filtered_count="results_count"
          @filter="on_filter_change"
          @availability="on_availability_change"
          @reset="on_reset"
          @close="toggle_filters"
        />
      </b-col>

      <b-col cols="12" xl="9">
        <results-controls
          v-if="!loading"
          :sort="sort"
          @order="on_order"
          @toggle-filters="toggle_filters"
        >
          <template #heading>
            <h1 class="vb-heading vb-result__title">{{ location.label }}</h1>
            <p v-if="is_only_stay_package">
              {{ occupancy[0].adults + occupancy[0].children.length }} ospiti -
              {{ formatted_checkin }}
              -
              {{ formatted_checkout }}
            </p>
          </template>
        </results-controls>

        <dynamic-scroller
          v-if="!loading"
          ref="results-scroller"
          page-mode
          :items="filtered_products"
          :min-item-size="204"
          :emit-update="true"
          key-field="pidkey"
          @update="on_scroll_update"
        >
          <template v-slot="{ item, index, active }">
            <dynamic-scroller-item
              class="pb-4"
              :item="item"
              :data-index="index"
              :active="active"
              :size-dependencies="[]"
            >
              <pad
                v-if="item.type === 'product'"
                :product="item"
                :statics="statics[item.pid]"
                :occupancy="occupancy"
                :keyds="keyds"
                :brand="get_brand(item.pid.split('-')[1])"
              />
              <cta-credit v-if="item.type === 'credit'" />
            </dynamic-scroller-item>
          </template>
        </dynamic-scroller>
      </b-col>
    </b-row>
  </div>
</template>

<script>
  import { parse_date, deep_clone, custom_event } from '@utilities/commons';
  import { get_brand } from '@alpitour/resources';
  import {
    apply_filters,
    build_filters,
  } from '@alpitour/utilities/results-filters';
  import {
    apply_sort,
    inject_credit_pads,
  } from '@alpitour/utilities/results-sorting';
  import ResultsControls from './results-controls/results-controls.vue';
  import FiltersSidebar from './results-controls/filters-sidebar.vue';
  import CtaCredit from '@alpitour/components/blocks/cta-credit';
  import NotFound from '@/components/blocks/not-found';
  import { mapState, mapGetters } from 'vuex/dist/vuex.common.js';

  export default {
    name: 'results-block',
    components: {
      'results-controls': ResultsControls,
      'filters-sidebar': FiltersSidebar,
      'cta-credit': CtaCredit,
      'not-found': NotFound,
    },
    data() {
      return {
        location: this.$store.state.liveat.location,
        occupancy: deep_clone(this.$store.state.occupancy),
        checkin_date: this.$store.state.liveat.checkin_date,
        checkout_date: this.$store.state.liveat.checkout_date,
        loading: true,
        filtered_products: [],
        scroller_interval: null,
        filters: {},
        update_key: 0,
        show_filters: false,
      };
    },
    computed: {
      ...mapState('liveat', [
        'package_type',
        'products',
        'statics',
        'keyds',
        'filter_keyd',
        'filter_accommodation',
        'filter_rating',
        'filter_airport',
        'filter_brand',
        'filter_availability',
        'sort',
        'offset_scroll_search',
      ]),
      ...mapGetters('liveat', ['is_only_stay_package']),
      formatted_checkin() {
        return this.format_date(this.checkin_date);
      },
      formatted_checkout() {
        return this.format_date(this.checkout_date);
      },
      active_filters_map() {
        return {
          filter_keyd: this.filter_keyd,
          filter_accommodation: this.filter_accommodation,
          filter_rating: this.filter_rating,
          filter_airport: this.filter_airport,
          filter_brand: this.filter_brand,
          filter_availability: this.filter_availability,
        };
      },
      results_count() {
        return this.filtered_products.filter(
          (item) => item.type === 'product',
        ).length;
      },
    },
    watch: {
      products() {
        this.run_filter_pipeline();
      },
    },
    created() {
      this.parse_date = parse_date;
      this.get_brand = get_brand;
    },
    mounted() {
      this.desktop_media_query = window.matchMedia(
        `(min-width: ${this.sass('xl')})`,
      );
      if (this.desktop_media_query.addEventListener) {
        this.desktop_media_query.addEventListener(
          'change',
          this.close_filters_on_desktop,
        );
      } else {
        this.desktop_media_query.addListener(this.close_filters_on_desktop);
      }

      this.filtered_products = this.products;
      this.run_filter_pipeline();
      this.loading = false;
      if (this.products.length === 0) {
        custom_event('no_results', '', 0, '');
      }
      if (this.offset_scroll_search === 0) {
        return;
      }

      this.$nextTick(() => {
        this.scroller_interval = setInterval(() => {
          if (!this.$refs['results-scroller']) {
            this.clean_scroller_interval();
            return;
          }
          const { scroller } = this.$refs['results-scroller'].$refs || {};
          if (!scroller) {
            this.clean_scroller_interval();
            return;
          }
          if (scroller.ready) {
            this.clean_scroller_interval();
            scroller.scrollToPosition(this.offset_scroll_search);
          }
        }, 100);
      });
    },
    beforeDestroy() {
      this.clean_scroller_interval();
      if (this.desktop_media_query) {
        if (this.desktop_media_query.removeEventListener) {
          this.desktop_media_query.removeEventListener(
            'change',
            this.close_filters_on_desktop,
          );
        } else {
          this.desktop_media_query.removeListener(
            this.close_filters_on_desktop,
          );
        }
      }
      if (this.show_filters) {
        document.body.classList.remove('noscroll');
      }
    },
    methods: {
      format_date(date_string) {
        const d = parse_date(date_string);
        return `${d.day_number} ${d.month_string} ${d.year}`;
      },
      on_order(value) {
        this.$store.commit('liveat/set_store_values', { sort: value });
        this.run_filter_pipeline();
      },

      on_filter({ key, filters }) {
        this.$store.commit('liveat/set_store_values', {
          [`filter_${key}`]: filters,
        });
        this.run_filter_pipeline();
      },

      on_filter_change({ key }) {
        const options = this.filters[key] || [];
        const filters = options
          .filter((option) => option.selected)
          .flatMap((option) =>
            Array.isArray(option.value) ? option.value : [option.value],
          );

        this.on_filter({ key, filters });
      },

      on_availability_change(value) {
        this.on_filter({ key: 'availability', filters: value });
      },

      on_reset() {
        this.$store.commit('liveat/set_store_values', {
          filter_keyd: [],
          filter_accommodation: [],
          filter_rating: [],
          filter_airport: [],
          filter_brand: [],
          filter_availability: false,
        });
        this.run_filter_pipeline();
      },

      toggle_filters() {
        this.show_filters = !this.show_filters;
        document.body.classList.toggle('noscroll', this.show_filters);
      },

      close_filters_on_desktop(event) {
        if (!event.matches || !this.show_filters) {
          return;
        }
        this.show_filters = false;
        document.body.classList.remove('noscroll');
      },

      refresh_filters() {
        const { result: brand_scope } = apply_filters({
          products: this.products,
          active_filters: { ...this.active_filters_map, filter_brand: [] },
          availability_total: this.get_availability().total,
          markup_fn: (price) => this.markup(price),
        });

        this.filters = build_filters({
          products: this.products,
          filtered: this.filtered_products,
          statics: this.statics,
          active_filters: this.active_filters_map,
          is_only_stay: this.is_only_stay_package,
          brand_scope: brand_scope || [],
        });
        this.update_key = this.update_key + 1;
      },

      run_filter_pipeline() {
        const availability_total = this.get_availability().total;

        const { result, empty_filter } = apply_filters({
          products: this.products,
          active_filters: this.active_filters_map,
          availability_total,
          markup_fn: (price) => this.markup(price),
        });

        if (result === null) {
          this.$nextTick(() => {
            this.on_filter({ key: empty_filter, filters: [] });
          });
          return;
        }

        const sorted = apply_sort(result, this.sort);
        this.filtered_products = inject_credit_pads(sorted);
        this.refresh_filters();
      },

      on_scroll_update() {
        if (!this.$refs['results-scroller']) {
          return;
        }
        const { scroller } = this.$refs['results-scroller'].$refs || {};
        if (!scroller) {
          return;
        }
        const { start: scroll = 0 } = scroller.getScroll();
        if (scroll === 0) {
          return;
        }
        this.$store.commit('liveat/set_store_values', {
          offset_scroll_search: scroll,
        });
      },

      clean_scroller_interval() {
        if (this.scroller_interval) {
          clearInterval(this.scroller_interval);
          this.scroller_interval = null;
        }
      },
    },
  };
</script>

<style lang="scss">
  .vb-result {
    &__title {
      font-size: $h3-font-size;
      margin-bottom: 0;
      text-transform: lowercase;

      &:first-letter {
        text-transform: uppercase;
      }
    }

    &__sidebar {
      @include media-breakpoint-down(lg) {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        max-width: none;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background-color: $vb-body-bg;
        transform: translateX(100%);
        transition: $transition-base;
        z-index: 999;

        &.visible {
          transform: translateX(0);
        }
      }
    }
  }
</style>
